// One hook for the Classrooms rollout singleton (public.classroom_settings,
// migration 20261016120000_classrooms_gate.sql) and the Memories switch
// (feature_flags row classrooms_memories_enabled), shared by the Classrooms tab.
//
// Modelled on community_manager/useCmSettings.ts: a single settings row read
// and written directly (admin-only RLS on select and update; there is no RPC),
// so a change is one PATCH and can never half-apply. Two rules carried over:
//
// 1. UNREADABLE IS NOT OFF. `settings` is null and `unreadable` true when the
//    row cannot be read — an RLS or network error, or a missing row. The page
//    must say so rather than draw an "Off" it does not know to be true: an
//    admin who sees a stale "Off" and widens to Pilot is exactly the mistake
//    this prevents. (The server treats a missing row as closed —
//    classroom_open() coalesces to false — but the dashboard must not guess.)
// 2. `memoriesEnabled` is null (not false) for the same reason: a missing or
//    unreadable flag row disables the switch instead of pretending Memories
//    are off.
//
// `save` and `setMemories` throw on error and re-read afterwards, so the page
// always renders what the database holds, not what it asked for. The
// touch trigger (classroom_settings_touch) stamps updated_at/updated_by and
// forces id := true; the row is also audited (admin_audit_enable 'all').
//
// Additive since (classrooms-audit-admin-classrooms):
// A1. `save`/`setMemories` treat a 0-row write (RLS silently matched
//     nothing — e.g. the caller is no longer an admin) as an error: they
//     re-read the truth with `refetch()` and then throw NOTHING_WRITTEN,
//     the same way a real Postgres error is thrown.
// A2. `upstream` reads the flags/rollout that gate Classrooms from ABOVE
//     (public.groups_enabled, groups_class_chapters_enabled and
//     groups_rollout.mode — see _classroom_visible_to in
//     20261018120000_groups_audience_core.sql). A failure to read any one
//     of them never makes `settings` itself unreadable — they are reported
//     independently so the page can say exactly what it does and doesn't
//     know.
// A3. `changedAt`/`changedBy` surface classroom_settings.updated_at/
//     updated_by (the dashboard's own audit trail). The touch trigger
//     (classroom_settings_touch, 20261016120000_classrooms_gate.sql) sets
//     updated_by := coalesce(auth.uid(), old.updated_by) on every write, so
//     a null updated_by proves the row was never written from here, not
//     that nobody has changed it.
// A4. A `window` focus listener re-reads everything (classroom_settings has
//     no Realtime), guarded by a request counter so a slow, stale refetch
//     can never clobber a newer one.
// A5. `save` takes an optional `expectedMode`, added to the update as
//     `.eq('mode', expectedMode)` — the "second admin / stale tab" guard for
//     a no-dialog write a caller issues BECAUSE it believes the mode isn't
//     what it's about to become (list.tsx's onYearsChange staging path). A
//     drifted server mode then matches zero rows instead of silently
//     widening what the caller thought was a no-op stage.

import { useCallback, useEffect, useRef, useState } from 'react'
import { supabaseClient } from '../../lib/supabaseClient'

/** off → admins → pilot → everyone (the CHECK on classroom_settings.mode). */
export type ClassroomMode = 'off' | 'admins' | 'pilot' | 'everyone'

export interface ClassroomSettings {
  mode: ClassroomMode
  /** Four-digit class years; the CHECK classroom_years_valid() rejects anything else. */
  pilot_years: string[]
}

export const MEMORIES_FLAG_KEY = 'classrooms_memories_enabled'

/** Thrown by `save`/`setMemories` when the write matched zero rows (A1). */
export const NOTHING_WRITTEN = 'nothing was written (you may no longer be an admin). Reload the page and try again.'

/** admins → testers → everyone (public.groups_rollout.mode's CHECK). */
export type GroupsAudienceMode = 'admins' | 'testers' | 'everyone'

/**
 * The flags/rollout that gate Classrooms from ABOVE, mirrored from
 * _classroom_visible_to (20261018120000_groups_audience_core.sql): every
 * Classroom is closed, admins included, unless both groups_enabled and
 * groups_class_chapters_enabled are on.
 */
export interface ClassroomUpstream {
  /** feature_flags.groups_enabled. null = couldn't read; a MISSING row is false (groups_raw_flag coalesces to false). */
  groupsOn: boolean | null
  /** feature_flags.groups_class_chapters_enabled, same rules. */
  chaptersOn: boolean | null
  /** groups_rollout.mode. null = couldn't read or unexpected value; a MISSING row is 'admins' (_groups_rollout_mode). */
  groupsAudience: GroupsAudienceMode | null
}

const UPSTREAM_UNKNOWN: ClassroomUpstream = { groupsOn: null, chaptersOn: null, groupsAudience: null }

export interface ClassroomSettingsState {
  settings: ClassroomSettings | null
  /** True when the settings row could not be read (error or missing row) — never shown as "Off". */
  unreadable: boolean
  /** True until the first read finishes. */
  loading: boolean
  refetch: () => Promise<void>
  /** `expectedMode` (A5): when passed, the write is conditioned on the server still being in that mode — see the module doc above `save`'s implementation. */
  save: (patch: Partial<ClassroomSettings>, expectedMode?: ClassroomMode) => Promise<void>
  /** null when the flag row is missing or unreadable. */
  memoriesEnabled: boolean | null
  setMemories: (on: boolean) => Promise<void>
  /** The flags/rollout that gate Classrooms from above (A2). */
  upstream: ClassroomUpstream
  /** classroom_settings.updated_at, or null before the row has ever been written. */
  changedAt: string | null
  /** The last DASHBOARD writer's name. The classroom_settings_touch trigger stamps updated_at/updated_by
   * on every write, but updated_by is null for SQL writes (service_role has no auth.uid()); this is
   * null when updated_by is null, or 'Unknown admin' on a failed lookup. */
  changedBy: string | null
}

interface ClassroomSettingsRow {
  mode: ClassroomMode
  pilot_years: string[]
  updated_at: string
  updated_by: string | null
}

export function useClassroomSettings(): ClassroomSettingsState {
  const [settings, setSettings] = useState<ClassroomSettings | null>(null)
  const [unreadable, setUnreadable] = useState(false)
  const [memoriesEnabled, setMemoriesEnabled] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)
  const [upstream, setUpstream] = useState<ClassroomUpstream>(UPSTREAM_UNKNOWN)
  const [changedAt, setChangedAt] = useState<string | null>(null)
  const [changedBy, setChangedBy] = useState<string | null>(null)
  const requestRef = useRef(0)

  const refetch = useCallback(async () => {
    const requestId = ++requestRef.current
    try {
      const [
        { data: s, error: se },
        { data: f, error: fe },
        { data: r, error: re },
      ] = await Promise.all([
        supabaseClient
          .from('classroom_settings')
          .select('mode, pilot_years, updated_at, updated_by')
          .eq('id', true)
          .maybeSingle(),
        supabaseClient
          .from('feature_flags')
          .select('key, enabled')
          .in('key', [MEMORIES_FLAG_KEY, 'groups_enabled', 'groups_class_chapters_enabled']),
        supabaseClient.from('groups_rollout').select('mode').eq('id', true).maybeSingle(),
      ])

      const row = se ? null : ((s as ClassroomSettingsRow | null) ?? null)
      const nextSettings: ClassroomSettings | null = row ? { mode: row.mode, pilot_years: row.pilot_years } : null
      const nextUnreadable = row === null
      const nextChangedAt = row ? row.updated_at : null
      const updatedBy = row ? row.updated_by : null

      const flagsByKey = new Map<string, boolean>()
      if (!fe) {
        for (const flag of (f ?? []) as { key: string; enabled: boolean }[]) {
          flagsByKey.set(flag.key, flag.enabled)
        }
      }
      const nextMemories = fe || !flagsByKey.has(MEMORIES_FLAG_KEY) ? null : flagsByKey.get(MEMORIES_FLAG_KEY) === true
      // A MISSING row reads as false — groups_raw_flag() coalesces a missing
      // row to false, and this must overstate nothing.
      const groupsOn = fe ? null : (flagsByKey.get('groups_enabled') ?? false)
      const chaptersOn = fe ? null : (flagsByKey.get('groups_class_chapters_enabled') ?? false)

      let groupsAudience: GroupsAudienceMode | null
      if (re) {
        groupsAudience = null
      } else {
        // A MISSING row reads as 'admins' — _groups_rollout_mode() coalesces it.
        const mode = r ? (r as { mode: string }).mode : 'admins'
        groupsAudience = mode === 'admins' || mode === 'testers' || mode === 'everyone' ? mode : null
      }

      let nextChangedBy: string | null = null
      if (updatedBy) {
        try {
          const { data: profiles, error: pErr } = await supabaseClient
            .from('profiles')
            .select('user_id, name')
            .in('user_id', [updatedBy])
          if (pErr) throw pErr
          const match = (profiles ?? []).find((p) => p.user_id === updatedBy)
          nextChangedBy = match?.name || 'Unknown admin'
        } catch {
          nextChangedBy = 'Unknown admin'
        }
      }

      if (requestId !== requestRef.current) return
      setSettings(nextSettings)
      setUnreadable(nextUnreadable)
      setMemoriesEnabled(nextMemories)
      setUpstream({ groupsOn, chaptersOn, groupsAudience })
      setChangedAt(nextChangedAt)
      setChangedBy(nextChangedBy)
    } catch {
      if (requestId !== requestRef.current) return
      setSettings(null)
      setUnreadable(true)
      setMemoriesEnabled(null)
      setUpstream(UPSTREAM_UNKNOWN)
      setChangedAt(null)
      setChangedBy(null)
    } finally {
      if (requestId === requestRef.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refetch()
  }, [refetch])

  // classroom_settings has no Realtime (A4): a mode/year change applies on
  // the server at once, but a running dashboard only catches up when it
  // re-reads — so re-read whenever this tab regains focus.
  useEffect(() => {
    const onFocus = () => {
      void refetch()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [refetch])

  const save = useCallback(
    // `expectedMode` (A5) guards a WRITE issued from a client decision that
    // read `settings.mode` a while ago — e.g. list.tsx's onYearsChange, which
    // stages pilot years with no confirm dialog exactly because it believes
    // the mode isn't 'pilot' yet. classroom_settings has no Realtime (A4), so
    // a second admin can flip the server to 'pilot' while this tab sits
    // unfocused; without this guard the stale write would silently OPEN
    // those years to members instead of merely staging them, with no dialog
    // and a false "these open when you switch to Pilot" toast. Passing it
    // adds `.eq('mode', expectedMode)`, so a drifted mode makes the update
    // match zero rows — the existing NOTHING_WRITTEN path below then re-reads
    // the true row instead of letting the stale write appear to succeed.
    async (patch: Partial<ClassroomSettings>, expectedMode?: ClassroomMode) => {
      let query = supabaseClient.from('classroom_settings').update(patch).eq('id', true)
      if (expectedMode) query = query.eq('mode', expectedMode)
      const { data, error } = await query.select('id')
      if (error) throw new Error(error.message)
      if (!data || data.length === 0) {
        await refetch()
        throw new Error(NOTHING_WRITTEN)
      }
      await refetch()
    },
    [refetch],
  )

  const setMemories = useCallback(
    async (on: boolean) => {
      const { data, error } = await supabaseClient
        .from('feature_flags')
        .update({ enabled: on })
        .eq('key', MEMORIES_FLAG_KEY)
        .select('key')
      if (error) throw new Error(error.message)
      if (!data || data.length === 0) {
        await refetch()
        throw new Error(NOTHING_WRITTEN)
      }
      await refetch()
    },
    [refetch],
  )

  return {
    settings,
    unreadable,
    loading,
    refetch,
    save,
    memoriesEnabled,
    setMemories,
    upstream,
    changedAt,
    changedBy,
  }
}
